import React, { useEffect, useRef, useState } from "react";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "components/common/Toast";
import translate from "../../../i18n/translate";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  IconButton,
  DialogContentText,
  Switch,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WelcomePageIcon from "@mui/icons-material/Home";

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(true);
  const intervalIdRef = useRef(null);
  
  // Welcome page state
  const [welcomePageEnabled, setWelcomePageEnabled] = useState(false);
  const [welcomePageContent, setWelcomePageContent] = useState("");
  const [welcomePageDialogOpen, setWelcomePageDialogOpen] = useState(false);
  const [welcomePageLoading, setWelcomePageLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWelcomePageSettings();
  }, []);

  const fetchSettings = () => {
    setSettings([]);
    setIsLoading(false);
  };

  const handleEditClick = (data) => {
    setEditData(data);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setDialogOpen(false);
    try {
      const res = await ApiRequest.post(
        `get_settings_items?action=edit&index=${editData.index}&new_value=${editData.value}`
      );
      if (["failed", "fail"].includes(res.data.status)) {
        throw new Error("Update Failed!");
      }
      Toast.fire({ title: "Update Successful!", icon: "success" });
      fetchSettings();
    } catch (error) {
      Toast.fire({ title: "Update Failed!", icon: "error" });
    } finally {
      setIsLoading(false);
      handleDialogClose();
    }
  };

  const handleDeleteClick = (setting) => {
    setDeleteCandidate(setting);
    setDeleteDialogOpen(true);
  };

  const startCountdown = () => {
    setConfirmDisabled(true);
    setCountdown(10);
    clearInterval(intervalIdRef.current); // Clear any existing interval just in case
    intervalIdRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(intervalIdRef.current); // Clear interval when countdown reaches 0
          setIsConfirmed(true); // State to trigger confirmation logic
          setConfirmDisabled(false);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const handleConfirmDelete = () => {
    if (isConfirmed) executeDeletion();
    else startCountdown();
  };

  const executeDeletion = async () => {
    setDeleteDialogOpen(false);
    if (isConfirmed) {
      // Only execute if countdown is zero
      setIsLoading(true);
      try {
        const res = await ApiRequest.post(
          `get_settings_items?action=delete&index=${deleteCandidate.index}`
        );
        if (["failed", "fail"].includes(res.data.status)) {
          throw new Error("Update Failed!");
        }
        Toast.fire({ title: "Update Successful!", icon: "success" });
        fetchSettings();
      } catch (error) {
        Toast.fire({ title: translate("Deletion Failed") + "!", icon: "error" });
      } finally {
        setIsLoading(false);
        setConfirmDisabled(false);
        setIsConfirmed(false);
      }
    }
  };

  const handleCloseDeletionDialog = () => {
    clearInterval(intervalIdRef.current); // Clear the countdown interval
    setIsConfirmed(false); // Set to false to prevent deletion when countdown reaches 0
    setDeleteDialogOpen(false);
    setConfirmDisabled(false);
    setCountdown(10); // Reset countdown on successful or aborted deletion
  };

  // Welcome page functions
  const fetchWelcomePageSettings = async () => {
    try {
      const response = await ApiRequest.post("welcome_page?operation=get");
      if (response.data.status === "success") {
        setWelcomePageEnabled(response.data.enabled === true || response.data.enabled === "true");
        setWelcomePageContent(response.data.content || "");
      }
    } catch (error) {
      console.error("Failed to fetch welcome page settings:", error);
    }
  };

  const handleWelcomePageToggle = async () => {
    const newEnabled = !welcomePageEnabled;
    setWelcomePageLoading(true);
    try {
      const response = await ApiRequest.post(
        `welcome_page?operation=update&enabled=${newEnabled}&content=${encodeURIComponent(welcomePageContent)}`
      );
      if (response.data.status === "success") {
        setWelcomePageEnabled(newEnabled);
        Toast.fire({ 
          title: newEnabled ? translate("Welcome page enabled") : translate("Welcome page disabled"), 
          icon: "success" 
        });
        if (newEnabled && !welcomePageContent.trim()) {
          setWelcomePageDialogOpen(true);
        }
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      Toast.fire({ title: translate("Update Failed") + "!", icon: "error" });
    } finally {
      setWelcomePageLoading(false);
    }
  };

  const handleWelcomePageContentSave = async () => {
    if (welcomePageContent.length > 500) {
      Toast.fire({ 
        title: translate("Content must be 500 characters or less"), 
        icon: "error" 
      });
      return;
    }
    setWelcomePageLoading(true);
    try {
      const response = await ApiRequest.post(
        `welcome_page?operation=update&enabled=${welcomePageEnabled}&content=${encodeURIComponent(welcomePageContent)}`
      );
      if (response.data.status === "success") {
        Toast.fire({ title: translate("Update Successful") + "!", icon: "success" });
        setWelcomePageDialogOpen(false);
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      Toast.fire({ title: translate("Update Failed") + "!", icon: "error" });
    } finally {
      setWelcomePageLoading(false);
    }
  };
  return (
    <div
      style={{
        margin: "20px auto",
        padding: "30px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
        borderRadius: "10px",
        width: "100%",
      }}
    >
      <Typography variant="h4" style={{ marginBottom: "20px" }}>
        System Settings
      </Typography>
      
      {/* Welcome Page Settings Card */}
      <Card
        elevation={3}
        sx={{
          marginBottom: 3,
          padding: 2,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
            <WelcomePageIcon sx={{ fontSize: 32, color: "primary.main" }} />
            <Typography variant="h6">{translate("Welcome Page")}</Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={welcomePageEnabled}
                onChange={handleWelcomePageToggle}
                disabled={welcomePageLoading}
                color="primary"
              />
            }
            label={welcomePageEnabled ? translate("Enabled") : translate("Disabled")}
          />
          {welcomePageEnabled && (
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                {translate("Welcome Page Content")} ({welcomePageContent.length}/500)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={welcomePageContent}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setWelcomePageContent(e.target.value);
                  }
                }}
                placeholder={translate("Enter welcome message (max 500 characters)")}
                variant="outlined"
                helperText={`${welcomePageContent.length}/500 ${translate("characters")}`}
                sx={{ marginBottom: 2 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => setWelcomePageDialogOpen(true)}
                disabled={welcomePageLoading}
              >
                {translate("Edit Content")}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {settings.map((setting) => (
            <Grid item xs={12} sm={6} md={4} key={setting.index}>
              <Card
                elevation={3}
                sx={{
                  minHeight: "200px",
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="ActiveCaption">
                    {setting.index} : {setting.settings}
                  </Typography>
                  <Typography variant="body2">{setting.description}</Typography>
                  <Typography variant="subtitle1" style={{ marginTop: "10px" }}>
                    Value: {setting.value}
                    <IconButton
                      onClick={() => handleEditClick(setting)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(setting)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth>
        <DialogTitle>Edit Value</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="New Value"
            type="text"
            fullWidth
            variant="outlined"
            value={editData?.value || ""}
            onChange={(e) =>
              setEditData({ ...editData, value: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{translate("Cancel")}</Button>
          <Button onClick={handleSave}>{translate("Save")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeletionDialog}>
        <DialogTitle>{translate("Confirm Deletion")}</DialogTitle>
        <DialogContent>
          {!isConfirmed && (
            <Box sx={{ display: "flex", alignItems: "flexStart", gap: 1 }}>
              <WarningAmberIcon color="warning" /> {/* Warning icon */}
              <DialogContentText>
                {translate("Are you sure you want to delete this setting")}? {translate("This action cannot be undone")}.
                {countdown > 0 ? ` ${translate("Deleting in")} ${countdown} ${translate("seconds")}...` : ""}
              </DialogContentText>
            </Box>
          )}

          {isConfirmed && (
            <Box sx={{ display: "flex", alignItems: "flexStart", gap: 1 }}>
              <CheckCircleIcon color="primary" /> {/* Confirmation icon */}
              <DialogContentText>
                {translate("Please click Confirm again to proceed with the deletion")}. {translate("This is to ensure that the deletion is intentional")}.
              </DialogContentText>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeletionDialog} color="primary">
            {translate("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            autoFocus
            disabled={confirmDisabled}
          >
            {isConfirmed ? translate("Confirm again") : translate("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Welcome Page Content Dialog */}
      <Dialog 
        open={welcomePageDialogOpen} 
        onClose={() => setWelcomePageDialogOpen(false)} 
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{translate("Welcome Page Content")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={translate("Welcome Message")}
            type="text"
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            value={welcomePageContent}
            onChange={(e) => {
              if (e.target.value.length <= 500) {
                setWelcomePageContent(e.target.value);
              }
            }}
            helperText={`${welcomePageContent.length}/500 ${translate("characters")}`}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWelcomePageDialogOpen(false)}>
            {translate("Cancel")}
          </Button>
          <Button 
            onClick={handleWelcomePageContentSave} 
            variant="contained"
            disabled={welcomePageLoading}
          >
            {translate("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SystemSettings;
